<div class="call-logs-container">
  <!-- ========================================== -->
  <!-- SECTION 1: SKELETON LOADERS -->
  <!-- ========================================== -->

  <ng-container *ngIf="isLoading && isInitialLoad">
    <!-- Stats Skeleton -->
    <div class="mb-6">
      <app-skeleton-loader type="stats" [statsCount]="4"></app-skeleton-loader>
    </div>

    <!-- Filters and Search Bar Skeleton -->
    <div class="filters-container mb-6">
      <div class="filters-row">
        <!-- Date Range Filter Skeleton -->
        <div class="filter-item">
          <div class="h-10 bg-gray-100 rounded skeleton-shimmer"></div>
        </div>

        <!-- Telecaller Filter Skeleton -->
        <div class="filter-item" *ngIf="isTelecallerFilterVisible">
          <div class="h-10 bg-gray-100 rounded skeleton-shimmer"></div>
        </div>

        <!-- Search Bar Skeleton -->
        <div class="search-item">
          <div class="h-10 bg-gray-100 rounded skeleton-shimmer"></div>
        </div>
      </div>
    </div>

    <!-- Table Skeleton -->
    <div class="hidden md:block">
      <app-skeleton-loader
        type="table"
        [count]="5"
        [tableColumns]="['w-24', 'w-32', 'w-32', 'w-24', 'w-20', 'w-20', 'w-20', 'w-24', 'w-24', 'w-24', 'w-20']"
      ></app-skeleton-loader>
    </div>

    <!-- Mobile Cards Skeleton -->
    <div class="md:hidden">
      <app-skeleton-loader type="card" [count]="5"></app-skeleton-loader>
    </div>
  </ng-container>

  <!-- ========================================== -->
  <!-- SECTION 2: PAGE CONTENT -->
  <!-- ========================================== -->

  <!-- Stats Cards -->
  <div *ngIf="!isInitialLoad" class="stats-grid fade-in">
    <app-stats-card
      [icon]="'phoneCheck'"
      title="Total Calls"
      value="{{ stats.totalCalls }}"
      color="indigo"
    ></app-stats-card>

    <app-stats-card
      [icon]="'phoneRinging'"
      title="Total Call Duration"
      value="{{ stats.totalCallDuration }}"
      color="purple"
    ></app-stats-card>
    <app-stats-card
      [icon]="'phoneCalling'"
      title="Avg. Call Duration"
      value="{{ stats.avgCallDuration }}"
      color="violet"
    ></app-stats-card>
    <app-stats-card
      [icon]="'followUP'"
      title="Scheduled Calls"
      value="{{ stats.scheduledCalls }}"
      color="teal"
    ></app-stats-card>
  </div>

  <!-- Filters and Search Bar -->
  <div *ngIf="!isInitialLoad" class="filters-container fade-in">
    <div class="filters-row">
      <!-- Date Range Filter -->
      <div class="filter-item">
        <ng-select
          [(ngModel)]="selectedDateRanges"
          [items]="dateRangeOptions"
          bindLabel="name"
          placeholder="Date Range"
          [clearable]="true"
          [compareWith]="compareFn"
          (change)="onFiltersChange()"
          appendTo="body"
        >
        </ng-select>
      </div>

      <!-- Telecaller Filter -->
      <div class="filter-item" *ngIf="isTelecallerFilterVisible">
        <ng-select
          [(ngModel)]="selectedTelecallers"
          [items]="telecallerOptions"
          bindLabel="name"
          placeholder="Telecaller"
          [multiple]="true"
          [closeOnSelect]="false"
          [clearable]="false"
          [compareWith]="compareFn"
          (change)="onFiltersChange()"
          appendTo="body"
        >
          <ng-template ng-multi-label-tmp let-items="items">
            <div class="ng-value">
              <span class="ng-value-label">{{items.length}} {{items.length === 1 ? 'Telecaller' : 'Telecallers'}} Selected</span>
            </div>
          </ng-template>
        </ng-select>
      </div>

      <!-- Search Bar -->
      <div class="search-item">
        <div class="form-control relative w-full">
          <input
            placeholder="Search call logs..."
            type="text"
            class="input input-md max-w-full pl-10"
            [(ngModel)]="searchTerm"
            (input)="applyFilters()"
          />
          <span class="absolute inset-y-0 left-3 inline-flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
              <path d="M21 21l-6 -6" />
            </svg>
          </span>
        </div>
      </div>
    </div>

    <!-- Selected Filters Display -->
    <div class="selected-filters" *ngIf="selectedDateRanges || selectedTelecallers.length > 0 || selectedPrograms.length > 0">
      <div class="selected-filters-list">
        <!-- Date Range Tag (Single) -->
        @if (selectedDateRanges) {
          <span class="filter-tag">
            {{ selectedDateRanges.name }}
            <button class="remove-btn" (click)="removeFilter('dateRange', selectedDateRanges)">×</button>
          </span>
        }

        <!-- Telecaller Tags -->
        @for (telecaller of selectedTelecallers; track telecaller.id) {
          <span class="filter-tag telecaller-tag">
            {{ telecaller.name }}
            <button class="remove-btn" (click)="removeFilter('telecaller', telecaller)">×</button>
          </span>
        }

        <!-- Program Tags -->
        @for (program of selectedPrograms; track program.id) {
          <span class="filter-tag">
            {{ program.name }}
            <button class="remove-btn" (click)="removeFilter('program', program)">×</button>
          </span>
        }

        <!-- Clear All Button -->
        <button class="btn btn-secondary" (click)="clearAllFilters()">Clear</button>
      </div>
    </div>
  </div>

  <!-- Call Logs Table -->
  <div *ngIf="!isInitialLoad" [class.opacity-50]="isLoading">
    <!-- Desktop View -->
    <div class="rounded bg-white max-md:hidden">
      <table class="w-full text-left border-collapse text-sm">
        <thead class="border-b border-gray-200">
          <tr>
            <th class="table-header">Caller ID</th>
            <th class="table-header">Patient Name & ID</th>
            <th class="table-header">Telecaller Name & ID</th>
            <th class="table-header">Date</th>
            <th class="table-header">Start Time</th>
            <th class="table-header">End Time</th>
            <th class="table-header">Duration</th>
            <th class="table-header">Next Follow-up</th>
            <th class="table-header">Call Type</th>
            <th class="table-header">Outcome</th>
            <th class="table-header">Recording</th>
          </tr>
        </thead>
        <tbody>
          @for (log of paginatedCallLogs; track log.id) {
            <tr class="border-b hover:bg-gray-50 transition ripple group">
              <td class="table-item">
                <span class="text-xs font-mono">{{ log.callerId.substring(0, 8) }}...</span>
              </td>
              <td class="table-item">
                <div class="font-medium text-gray-900">{{ log.patientName }}</div>
                @if (log.patientId) {
                  <div class="text-xs text-gray-500">{{ log.patientId }}</div>
                }
              </td>
              <td class="table-item">
                <div class="font-medium text-gray-900">{{ log.telecallerName }}</div>
                @if (log.userId) {
                  <div class="text-xs text-gray-500">{{ log.userId }}</div>
                }
              </td>
              <td class="table-item">{{ log.date }}</td>
              <td class="table-item">{{ log.startTime }}</td>
              <td class="table-item">{{ log.endTime }}</td>
              <td class="table-item">{{ log.duration }}</td>
              <td class="table-item">{{ log.nextFollowUp || 'N/A' }}</td>
              <td class="table-item">
                <span class="inline-flex items-center gap-1.5">
                  @if (log.callType === 'Incoming') {
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  }
                  @if (log.callType === 'Outgoing') {
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M21 3l-6 6m6-6v4.5m0-4.5h-4.5" />
                    </svg>
                  }
                  <span>{{ log.callType }}</span>
                </span>
              </td>
              <td class="table-item">
                <app-badge
                  [text]="log.outcome"
                  [variant]="getOutcomeVariant(log.outcome)"
                  [size]="'sm'"
                ></app-badge>
              </td>
              <td class="table-item">
                @if (log.recordingUrl) {
                  <button
                    (click)="openAudioModal(log)"
                    class="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                    </svg>
                    Play
                  </button>
                } @else {
                  <span class="text-gray-400 text-sm">N/A</span>
                }
              </td>
            </tr>
          }
          @if (paginatedCallLogs.length === 0) {
            <tr>
              <td colspan="11" class="text-center py-8 text-gray-500">
                No call logs found
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Mobile View - Card Layout -->
    <div class="block md:hidden space-y-4">
      @for (log of paginatedCallLogs; track log.id) {
        <div class="bg-white rounded shadow-md border border-gray-200 p-4">
          <div class="space-y-3">
            <!-- Header -->
            <div class="border-b border-gray-200 pb-2 flex justify-between items-start">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">{{ log.patientName }}</h3>
                @if (log.patientId) {
                  <p class="text-xs text-gray-500">{{ log.patientId }}</p>
                }
                <p class="text-xs font-mono text-gray-500">Caller ID: {{ log.callerId.substring(0, 12) }}...</p>
              </div>
              <app-badge
                [text]="log.outcome"
                [variant]="getOutcomeVariant(log.outcome)"
                [size]="'sm'"
              ></app-badge>
            </div>

            <!-- Details -->
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Telecaller:</span>
                <div class="text-right">
                  <div class="text-gray-900 font-medium">{{ log.telecallerName }}</div>
                  @if (log.userId) {
                    <div class="text-xs text-gray-500">{{ log.userId }}</div>
                  }
                </div>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Date:</span>
                <span class="text-gray-900">{{ log.date }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Time:</span>
                <span class="text-gray-900">{{ log.startTime }} - {{ log.endTime }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Duration:</span>
                <span class="text-gray-900">{{ log.duration }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Call Type:</span>
                <span class="text-gray-900 inline-flex items-center gap-1.5">
                  @if (log.callType === 'Incoming') {
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  }
                  @if (log.callType === 'Outgoing') {
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M21 3l-6 6m6-6v4.5m0-4.5h-4.5" />
                    </svg>
                  }
                  <span>{{ log.callType }}</span>
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Next Follow-up:</span>
                <span class="text-gray-900">{{ log.nextFollowUp || 'N/A' }}</span>
              </div>
              <div class="flex justify-between items-center pt-2 mt-2 border-t border-gray-100">
                <span class="text-gray-600">Recording:</span>
                @if (log.recordingUrl) {
                  <button
                    (click)="openAudioModal(log)"
                    class="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
                  >
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                    </svg>
                    Play
                  </button>
                } @else {
                  <span class="text-gray-400 text-sm">Not available</span>
                }
              </div>
            </div>
          </div>
        </div>
      }
      @if (paginatedCallLogs.length === 0) {
        <div class="text-center py-8 text-gray-500 bg-white rounded border border-gray-200">
          No call logs found
        </div>
      }
    </div>
  </div>

  <!-- Pagination -->
  <app-pagination
    *ngIf="!isInitialLoad"
    [currentPage]="currentPage"
    [totalPages]="totalPages"
    [pageSize]="rowsPerPage"
    [pageSizes]="paginationSizes"
    [totalItems]="paginationTotalItems"
    [isLoading]="isLoading"

    [isEmpty]="isEmpty"
    [hideOnMobile]="false"
    (pageChange)="onPageChange($event)"
    (pageSizeChange)="onPageSizeChange($event)"
    (goToPageClick)="goToPage($event)"
  ></app-pagination>

  <!-- ========================================== -->
  <!-- SECTION 3: MODALS -->
  <!-- ========================================== -->

  <!-- Audio Player Modal -->
  <app-audio-player-modal
    [isOpen]="showAudioModal"
    [audioUrl]="selectedRecordingUrl"
    [title]="'Call Recording'"
    [subtitle]="selectedCallInfo ? selectedCallInfo.patientName + ' • ' + selectedCallInfo.date + ' • ' + selectedCallInfo.duration : ''"
    (close)="closeAudioModal()"
  ></app-audio-player-modal>
</div>
